import { ModuleMessage, QueuedModuleMessage } from './types';
import _noop from 'lodash/noop';
import _uniqBy from 'lodash/uniqBy';
import React, { useContext } from 'react';
import { SelectedDashboardConfigContext } from '../util/DashboardsContextRoot';
import { v4 as uuid } from 'uuid';
import { useCustomCompareEffect, useMount } from 'react-use';
import { useModuleKey } from '../ModuleKeyContext';

export type ModuleMessagingContextValue = {
  messages: QueuedModuleMessage[];
  onRemoveMessage: (messageId: string) => void;
  onSendMessage: (message: ModuleMessage) => void;
};

const emptyMessages: QueuedModuleMessage[] = [];

export const ModuleMessagingContext =
  React.createContext<ModuleMessagingContextValue>({
    messages: emptyMessages,
    onRemoveMessage: _noop,
    onSendMessage: _noop,
  });

export type ModuleMessagingProviderProps = {
  children: React.ReactNode;
};

export const ModuleMessagingProvider: React.FC<
  ModuleMessagingProviderProps
> = ({ children }) => {
  const selectedDashboardConfig = useContext(SelectedDashboardConfigContext);
  const [messages, setMessages] = React.useState<QueuedModuleMessage[]>();

  const handleRemoveMessage = React.useCallback(
    (messageId: string) => {
      setMessages(messages =>
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        messages.filter(message => message.messageId !== messageId),
      );
    },
    [setMessages],
  );
  const handleSendMessage = React.useCallback(
    (message: ModuleMessage) => {
      const allModulesOfType = selectedDashboardConfig?.modules.filter(
        _ => _.module.moduleType === message.targetModuleType,
      );

      if (!allModulesOfType?.length) {
        return;
      }

      const moduleKeys = allModulesOfType.map(_ => _.module.key);
      const messagePerModule = moduleKeys.map<QueuedModuleMessage>(key => ({
        ...message,
        moduleKey: key,
        messageId: uuid(),
      }));
      setMessages(messages =>
        _uniqBy(
          [...(messages || []), ...messagePerModule],
          _ => _.targetModuleType + _.moduleKey + (_.deduplicationKey || ''),
        ),
      );
    },
    [setMessages, selectedDashboardConfig],
  );

  const contextValue = React.useMemo<ModuleMessagingContextValue>(
    () => ({
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      messages,
      onRemoveMessage: handleRemoveMessage,
      onSendMessage: handleSendMessage,
    }),
    [messages, handleRemoveMessage, handleSendMessage],
  );

  return (
    <ModuleMessagingContext.Provider value={contextValue}>
      {children}
    </ModuleMessagingContext.Provider>
  );
};

export const useSendModuleMessage = () => {
  const { onSendMessage } = useContext(ModuleMessagingContext);
  return onSendMessage;
};

export type UseOnReceiveModuleMessageArgs = {
  /**
   * Callback fired when a message is received.
   */
  onReceiveMessage: (message: ModuleMessage) => void;
  /**
   * Pre-filter module messages.  Module messages not passing filter will not
   * trigger callback and will not be dequeued
   */
  filterMessages?: (message: ModuleMessage) => boolean;
};

export const useOnReceiveModuleMessage = ({
  filterMessages,
  onReceiveMessage,
}: UseOnReceiveModuleMessageArgs) => {
  const { messages, onRemoveMessage } = useContext(ModuleMessagingContext);
  const [hasMounted, setHasMounted] = React.useState(false);
  const moduleKey = useModuleKey();

  useMount(() => {
    // remove stale messages
    const staleMessages =
      messages?.filter(
        _ =>
          _.moduleKey === moduleKey &&
          (!filterMessages || filterMessages(_)) &&
          !!_.replayOnMount,
      ) || [];

    staleMessages.forEach(_ => onRemoveMessage(_.messageId));

    setHasMounted(true);
  });

  const handleReceiveMessage = React.useCallback(
    (message: QueuedModuleMessage) => {
      onReceiveMessage(message);
      onRemoveMessage(message.messageId);
    },
    [onReceiveMessage, onRemoveMessage],
  );

  useCustomCompareEffect(
    () => {
      if (!hasMounted) return;

      const moduleMessages = messages?.filter(
        _ =>
          _.moduleKey === moduleKey && (!filterMessages || filterMessages(_)),
      );

      if (!moduleMessages?.length) {
        return;
      }

      const first = moduleMessages[0];
      handleReceiveMessage(first);
    },
    [messages, moduleKey, filterMessages, handleReceiveMessage, hasMounted],
    ([prevMessages], [curMessages]) => prevMessages === curMessages,
  );
};
